// @ts-nocheck
import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useCompanyMemberships } from "@/hooks/useCompanyMemberships";
import { supabase } from "@/integrations/supabase/client";
import { readWarm, writeWarm } from "@/lib/warm-cache";

interface CompanyInfo {
  id: string;
  name: string;
  code?: string;
  logo_url?: string;
  primary_color: string;
  secondary_color: string;
  is_trial?: boolean;
  /** 0=Sun..6=Sat. Absent on a company that predates the column; consumers go
   *  through useWorkweekStart(), which falls back to Sunday. */
  workweek_start?: number;
  /** false = store staff cannot @-tag an admin (Fresh Fill). Absent on a row
   *  loaded before the column existed; read via staffCanTagAdminsFrom(). */
  staff_can_tag_admins?: boolean;
}

interface CompanyContextType {
  companyInfo: CompanyInfo | null;
  loading: boolean;
  updateCompanyInfo: (updates: Partial<CompanyInfo>) => Promise<void>;
  refreshCompanyInfo: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const { profile } = useAuth();
  // Phase A.1 PR 3: primary membership wins, profile.company_id is the
  // fallback — identical for every current user.
  const { resolvedCompanyId } = useCompanyMemberships();

  const loadCompanyInfo = async () => {
    // Get company ID from either resolved membership, auth profile, or PIN session
    let companyId = resolvedCompanyId ?? profile?.company_id;
    
    if (!companyId) {
      try {
        const pinSession = localStorage.getItem('pin_session');
        if (pinSession) {
          const parsed = JSON.parse(pinSession);
          companyId = parsed.profile?.company_id;
          
          // For PIN sessions, use pre-loaded company data if available
          if (parsed.company) {
            console.log('[COMPANY_CONTEXT] Using pre-loaded company data from PIN session');
            setCompanyInfo(parsed.company);
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error('Error parsing PIN session for company info:', error);
      }
    }
    
    if (!companyId) {
      setLoading(false);
      return;
    }

    // Warm-start: paint the last-known company (keyed by company id) so the
    // dashboard skeleton doesn't wait on the network on a returning launch.
    // Cache miss → null → unchanged behavior below.
    const cached = await readWarm<CompanyInfo>('company', companyId);
    if (cached) {
      setCompanyInfo(cached);
      setLoading(false);
    }

    // Load company data from database for both email and PIN sessions
    // PIN sessions may not always have company data pre-loaded
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (data && !error) {
        console.log('[COMPANY_CONTEXT] Successfully loaded company data from database');
        setCompanyInfo(data);
        writeWarm('company', companyId, data); // write-through for next launch
      } else {
        console.error('[COMPANY_CONTEXT] Failed to load company data:', error);
      }
    } catch (error) {
      console.error('[COMPANY_CONTEXT] Error loading company info:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCompanyInfo = async (updates: Partial<CompanyInfo>) => {
    const targetCompanyId = resolvedCompanyId ?? profile?.company_id;
    if (!targetCompanyId || !companyInfo) return;

    try {
      const { error } = await supabase
        .from('companies')
        .update(updates)
        .eq('id', targetCompanyId);

      if (!error) {
        setCompanyInfo({ ...companyInfo, ...updates });
      } else {
        throw error;
      }
    } catch (error) {
      console.error('Error updating company info:', error);
      throw error;
    }
  };

  const refreshCompanyInfo = async () => {
    await loadCompanyInfo();
  };

  // Listen for profile changes, membership changes, and PIN session updates.
  //
  // Keyed on the EFFECTIVE id, not on its two inputs. Memberships resolve
  // after the profile, so resolvedCompanyId goes null -> X a beat later —
  // and for every single-company user X is the profile's own company_id.
  // Depending on both inputs re-ran the whole load (warm read + `companies`
  // query) on every launch to fetch the row it had just fetched (#1246).
  // A real company switch still changes the effective id and still reloads.
  const effectiveCompanyId = resolvedCompanyId ?? profile?.company_id;
  useEffect(() => {
    loadCompanyInfo();
  }, [effectiveCompanyId]);

  // Listen for PIN session updates
  useEffect(() => {
    const handlePinSessionUpdate = () => {
      loadCompanyInfo();
    };

    window.addEventListener('pin_session_updated', handlePinSessionUpdate);
    return () => {
      window.removeEventListener('pin_session_updated', handlePinSessionUpdate);
    };
  }, []);

  // CRITICAL FIX #3: Memoize context value to prevent unnecessary re-renders
  const value = React.useMemo(() => ({
    companyInfo,
    loading,
    updateCompanyInfo,
    refreshCompanyInfo
  }), [companyInfo, loading, updateCompanyInfo, refreshCompanyInfo]);

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}

/**
 * Like `useCompany`, but returns `undefined` outside a provider instead of
 * throwing.
 *
 * For leaf components that only want a company DETAIL and can render sensibly
 * without one — e.g. the schedule surfaces reading the workweek boundary, which
 * fall back to Sunday. Using the throwing version there would mean every test
 * that renders any schedule component has to wrap it in a CompanyProvider, and
 * a company that simply hasn't loaded yet would crash the page rather than
 * render a default week.
 */
export function useCompanyOptional() {
  return useContext(CompanyContext);
}